import api from '@services/api.service'
import { createAsyncThunk, EntityId } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { DjangoErrorResponse } from '@store/slices/generic.slice'
import { toast } from 'react-toastify'
import { NarrationModel, narrationUrl } from '@store/slices/narration.slice'
import { AudioSourceType } from '@shared/enums'
import { PromethistTranscription } from '@shared/classes'

export const updateNarration = createAsyncThunk(
  'narration/updateNarration',
  async (props: {
    narrationId: EntityId
    text?: string
    phonetic?: string
    ssml?: string
    dialogue?: PromethistTranscription
  }) => {
    if (!props.text && !props.phonetic && !props.ssml && !props.dialogue)
      throw new Error('Cannot update slot narration with no data!')
    try {
      const data: any = {}
      if (props.text !== undefined) data.text = props.text
      if (props.phonetic !== undefined) data.phonetic = props.phonetic
      if (props.ssml !== undefined) data.ssml = props.ssml
      if (props.dialogue !== undefined) data.dialogue = props.dialogue
      const response = await api.put(
        `${narrationUrl}${props.narrationId}/updateContent/`,
        {
          ...data,
        }
      )
      return response.data as NarrationModel
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        const serverError = axiosError.response.data as DjangoErrorResponse
        toast.error(`${serverError.message || 'Nepodařilo se uložit naraci.'}`)
      } else {
        toast.error('Nepodařilo uložit naraci')
      }
      throw axiosError
    }
  }
)

export const updateNarrationsVoice = createAsyncThunk(
  'narration/updateNarration',
  async (props: { narrationId: EntityId; voice: EntityId }) => {
    try {
      const data = { voice: props.voice }
      const response = await api.put(
        `${narrationUrl}${props.narrationId}/updateVoice/`,
        {
          ...data,
        }
      )
      return response.data as NarrationModel
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        const serverError = axiosError.response.data as DjangoErrorResponse
        toast.error(`${serverError.message || 'Nepodařilo se uložit naraci.'}`)
      } else {
        toast.error('Nepodařilo uložit naraci')
      }
      throw axiosError
    }
  }
)

export const generateAudio = createAsyncThunk(
  'narration/generateAudio',
  async (props: {
    narrationId: EntityId
    source: AudioSourceType
    filetype?: string
    normalization?: boolean
    dialogueNumber?: number
  }) => {
    try {
      const params: any = {
        source: props.source ? props.source : null,
      }
      if (props.filetype !== undefined) params.filetype = props.filetype
      if (props.dialogueNumber !== undefined)
        params.dialogueNumber = props.dialogueNumber
      if (props.normalization !== undefined)
        params.normalization = props.normalization
      const response = await api.get(
        `${narrationUrl}${props.narrationId}/generateAudio/`,
        { params }
      )
      return response.data as EntityId
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        const serverError = axiosError.response.data as DjangoErrorResponse
        toast.error(
          `${serverError.message || 'Nepodařilo se vygenerovat audio pro naraci'}`
        )
      } else {
        toast.error('Nepodařilo se vygenerovat audio pro naraci')
      }
      throw axiosError
    }
  }
)

export const publishNarration = createAsyncThunk(
  'narration/publish',
  async (props: { narrationId: EntityId }) => {
    try {
      const response = await api.get(
        `${narrationUrl}${props.narrationId}/publish/`
      )
      return response.data as EntityId
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        const serverError = axiosError.response.data as DjangoErrorResponse
        toast.error(
          `${serverError.message || 'Nepodařilo se publikovat naraci'}`
        )
      } else {
        toast.error('Nepodařilo se publikovat naraci')
      }
      throw axiosError
    }
  }
)

export const generateSsml = createAsyncThunk(
  'narration/generateSsml',
  async (props: {
    narrationId: EntityId
    source: AudioSourceType
    dialogueNumber?: number
  }) => {
    try {
      const params: any = {
        source: props.source ? props.source : null,
      }
      if (props.dialogueNumber !== undefined)
        params.dialogueNumber = props.dialogueNumber
      const response = await api.get(
        `${narrationUrl}${props.narrationId}/generateSsml/`,
        { params }
      )
      return response.data as NarrationModel
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        const serverError = axiosError.response.data as DjangoErrorResponse
        toast.error(
          `${serverError.message || 'Nepodařilo se vygenerovat ssml pro naraci'}`
        )
      } else {
        toast.error('Nepodařilo se vygenerovat ssml pro naraci')
      }
      throw axiosError
    }
  }
)
